import { useState, useEffect, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../context/UserContext";
import { useSessionValidation } from "../hooks/useSessionValidation";
import SearchField from "./SearchField";
import HeaderTable from "./HeaderTable";
import ScanFields from "./ScanFields";
import LinesTable from "./LinesTable";
import ActionButtons from "./ActionButtons";
import { Header, Line, ScanFields as ScanFieldsType } from "../types";
import { useSearchHeaders } from "../hooks/useSearchHeaders";
import { useLineDetails } from "../hooks/useLineDetails";
import { useLineManager } from "./LineManager";
import { useSerialNumberScanner } from "./SerialNumberScanner";
import { useRadSites } from "../hooks/useRadSites";
import { useSaveHeaderService } from "../services/saveHeaderService";
import { useUpsertLines } from "../hooks/useUpsertLines";
import { useStageSerialNumbers } from "../hooks/useStageSerialNumbers";
import { useSubmitRequest } from "../hooks/useSubmitRequest";
import { constructStageSerialJson } from "../services/stageSerialService";
import SerialSelectionPopup from "./SerialSelectionPopup";
import { useCreateLocator } from "../hooks/useCreateLocator";
import NewLineForm from "./NewLineForm";

interface SerialValidationResult {
  whid: string;
  organizationId: string;
  inventoryItemId: string;
  serialNumber: string;
  itemNumber: string;
  description: string;
  assetNumber: string;
  serialStatus: string;
  snRepairEligible: string;
}

const Dashboard = () => {
  const { userId, responsibilityId, sessionId } = useContext(UserContext);
  const { validateSession } = useSessionValidation();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const sessionValidated = useRef(false);
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedHeader, setSelectedHeader] = useState<Header | undefined>();
  const [scanFields, setScanFields] = useState<ScanFieldsType>({
    scanSerialNumber: "",
    toSubinventory: "",
    pallet: "",
    cage: "",
    tote: "",
    toLocator: "",
    trackingNumber: "",
  });
  const [isDarkTheme, setIsDarkTheme] = useState(false);
  const [updatedLines, setUpdatedLines] = useState<Line[]>([]);
  const [scannedLineIds, setScannedLineIds] = useState<Set<string>>(new Set());
  const [updatedHeaders, setUpdatedHeaders] = useState<Header[]>([]);
  const [originalToSite, setOriginalToSite] = useState<string | undefined>();
  const [showNewLineForm, setShowNewLineForm] = useState(false);
  const [serialNumberForForm, setSerialNumberForForm] = useState<string | undefined>();
  const [includeClosedOrders, setIncludeClosedOrders] = useState(false);
  const clearSerialNotFoundRef = useRef<(() => void) | null>(null);
  const clearLinesErrorsRef = useRef<(() => void) | null>(null);
  const [saveSuccessMessage, setSaveSuccessMessage] = useState<string | null>(null);
  const [warningMessage, setWarningMessage] = useState<string | null>(null);
  const [showSerialSelectionPopup, setShowSerialSelectionPopup] = useState(false);
  const [serialValidationResults, setSerialValidationResults] = useState<SerialValidationResult[]>([]);
  const [serialScanLoading, setSerialScanLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  const { headers: filteredHeaders, error, loading, searchHeaders, loadMoreHeaders, hasMore } = useSearchHeaders();
  const { lines: headerLines, error: linesError, loading: linesLoading } = useLineDetails(
    selectedHeader?.requestNumber
  );

  const currentLines = selectedHeader
    ? updatedLines.length > 0
      ? updatedLines
      : headerLines
    : [];
  
  console.log('Current lines for table:', currentLines);
  console.log('Updated lines length:', updatedLines.length);
  console.log('Header lines length:', headerLines.length);

  const lineManager = useLineManager({
    lines: currentLines,
    onLinesUpdate: setUpdatedLines,
    selectedHeaderToOrg: selectedHeader?.toOrg,
  });

  const serialScanner = useSerialNumberScanner({
    lines: currentLines,
    scanFields,
    onLinesUpdate: (lines) => {
      setUpdatedLines(lines);
      // Track the first line as scanned (it gets moved to top)
      if (lines.length > 0 && lines[0].validatedSerialNumber) {
        setScannedLineIds(prev => new Set(prev).add(lines[0].id));
      }
    },
    onScanFieldsUpdate: setScanFields,
    onSerialNotFound: (serialNumber) => {
      console.log('Opening form for serial number:', serialNumber);
      setSerialNumberForForm(serialNumber);
      setShowNewLineForm(true);
    },
    onMultipleSerialResults: (results) => {
      setSerialValidationResults(results);
      setShowSerialSelectionPopup(true);
    },
    selectedHeaderFromSite: selectedHeader?.fromSite,
    selectedHeaderToOrg: selectedHeader?.toOrg,
    organizationName: selectedHeader?.toSite,
    onLoadingChange: setSerialScanLoading,
    onShowWarning: (message: string) => {
      setWarningMessage(message);
      setTimeout(() => setWarningMessage(null), 3000);
    }
  });

  useEffect(() => {
    if (sessionId === "" || sessionId === undefined || sessionId === null) {
      navigate("/unauthorized");
    } else {
      if (!sessionValidated.current) {
        sessionValidated.current = true;
        validateSession(
          {
            user_id: parseInt(userId),
            resp_id: parseInt(responsibilityId),
            session_id: parseInt(sessionId),
          },
          (response: any) => {
            if (
              response &&
              response?.userId === Number(userId) &&
              response?.sessionId === Number(sessionId)
            ) {
              setIsLoggedIn(true);
            } else {
              navigate("/unauthorized");
            }
          }
        );
      }
    }
  }, [
    sessionId,
    navigate,
    responsibilityId,
    userId,
    validateSession,
  ]);

  useEffect(() => {
    document.body.style.backgroundColor = isDarkTheme ? "#1a1a1a" : "#ffffff";
    document.body.style.margin = "0";
    document.body.style.padding = "0";
    document.body.style.overflowX = "hidden";
    document.body.style.overflowY = "auto";
    document.documentElement.style.overflowX = "hidden";
    document.documentElement.style.overflowY = "auto";
  }, [isDarkTheme]);

  const handleSearchTermChange = (newSearchTerm: string) => {
    setSearchTerm(newSearchTerm);
    if (!newSearchTerm.trim()) {
      searchHeaders("", includeClosedOrders);
      setSelectedHeader(undefined);
      setScanFields({
        scanSerialNumber: "",
        toSubinventory: "",
        pallet: "",
        cage: "",
        tote: "",
        toLocator: "",
        trackingNumber: "",
      });
      setUpdatedLines([]);
    }
  };

  const handleSearch = () => {
    searchHeaders(searchTerm, includeClosedOrders);
    setSelectedHeader(undefined);
    setScanFields({ scanSerialNumber: "", toSubinventory: "", pallet: "", cage: "", tote: "", toLocator: "", trackingNumber: "" });
    setUpdatedLines([]);
  };

  const { data: radSites, isLoading: radSitesLoading, fetchRadSites } = useRadSites();
  const { saveHeader, error: saveError } = useSaveHeaderService();
  const { upsertLines, error: upsertError } = useUpsertLines();
  const { stageSerialNumbers, error: stageError } = useStageSerialNumbers();
  const { submitRequest, error: submitRequestError } = useSubmitRequest();
  const { createLocator } = useCreateLocator();

  const handleHeaderClick = (header: Header) => {
    console.log('Selected header data:', header);
    console.log('fromSite:', header.fromSite, 'toOrg:', header.toOrg);
    setSelectedHeader(header);
    setOriginalToSite(header.toSite);
    setScanFields({ scanSerialNumber: "", toSubinventory: "", pallet: "", cage: "", tote: "", toLocator: "", trackingNumber: "" });
    setUpdatedLines([]);
    setScannedLineIds(new Set());
    lineManager.clearNewRowIds();
  };

  const handleSave = async () => {
    setSaveLoading(true);
    try {
      const headerToUpdate = updatedHeaders.find(h => h.requestNumber === selectedHeader!.requestNumber) || selectedHeader!;
      const hasToSiteChanged = headerToUpdate.toSite !== originalToSite;
      
      let headerResult = { updatedToSite: headerToUpdate.toSite };
      
      // Only save header changes if To Site has changed
      if (hasToSiteChanged) {
        headerResult = await saveHeader({
          selectedHeader: selectedHeader!,
          updatedHeaders
        });
      }

      // Save line changes with "Draft" status
      if (updatedLines.length > 0) {
        await upsertLines(selectedHeader!.requestNumber, updatedLines, "Draft");
      }

      setSaveSuccessMessage("Changes Saved Successfully");
      setTimeout(() => setSaveSuccessMessage(null), 3000);
      setSelectedHeader(prev => prev ? { ...prev, toSite: headerResult.updatedToSite } : prev);
      setOriginalToSite(headerResult.updatedToSite);
      setScannedLineIds(new Set());
    } catch (error: any) {
      alert(`Save failed: ${error.message}`);
    } finally {
      setSaveLoading(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitLoading(true);
    try {
      const headerToUpdate = updatedHeaders.find(h => h.requestNumber === selectedHeader!.requestNumber) || selectedHeader!;
      const hasToSiteChanged = headerToUpdate.toSite !== originalToSite;
      
      let headerResult = { updatedToSite: headerToUpdate.toSite };
      
      // Only save header changes if To Site has changed
      if (hasToSiteChanged) {
        headerResult = await saveHeader({
          selectedHeader: selectedHeader!,
          updatedHeaders
        });
      }

      // Save line changes with "New" status
      if (updatedLines.length > 0) {
        await upsertLines(selectedHeader!.requestNumber, updatedLines, "New");
      }

      // Submit request as final step
      await submitRequest(
        selectedHeader!.requestNumber,
        userId,
        responsibilityId
      );

      setSaveSuccessMessage("Submitted Successfully");
      setTimeout(() => setSaveSuccessMessage(null), 3000);
      setSelectedHeader(prev => prev ? { ...prev, toSite: headerResult.updatedToSite } : prev);
      setOriginalToSite(headerResult.updatedToSite);
      setScannedLineIds(new Set());
    } catch (error: any) {
      alert(`Submit failed: ${error.message}`);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleCancel = () => {
    lineManager.cancelChanges();
    setScanFields({ scanSerialNumber: "", toSubinventory: "", pallet: "", cage: "", tote: "", toLocator: "", trackingNumber: "" });
    setScannedLineIds(new Set());
    setUpdatedHeaders([]); // Clear header changes to revert To Site values
    if (clearLinesErrorsRef.current) {
      clearLinesErrorsRef.current();
    }
  };

  if (!isLoggedIn) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <div
          style={{
            width: "40px",
            height: "40px",
            border: "4px solid #f3f3f3",
            borderTop: "4px solid #007bff",
            borderRadius: "50%",
            animation: "spin 1s linear infinite"
          }}
        />
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  const themeStyles = {
    backgroundColor: isDarkTheme ? "#1a1a1a" : "#ffffff",
    color: isDarkTheme ? "#ffffff" : "#000000",
  };

  return (
    <div
      style={{
        ...themeStyles,
        padding: "1px 20px 20px 20px",
        fontFamily: "Arial, sans-serif",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          backgroundColor: isDarkTheme ? "#2c3e50" : "#3498db",
          color: "white",
          padding: "15px 20px",
          margin: "-1px -20px 20px -20px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <h2 style={{ margin: 0, fontSize: "24px", fontWeight: "bold" }}>RMA Receive & Equipment Return Dashboard</h2>
        <div
          onClick={() => setIsDarkTheme(!isDarkTheme)}
          style={{
            width: "50px",
            height: "25px",
            backgroundColor: isDarkTheme ? "#007bff" : "#ccc",
            borderRadius: "25px",
            position: "relative",
            cursor: "pointer",
            transition: "background-color 0.3s",
            display: "flex",
            alignItems: "center",
            justifyContent: isDarkTheme ? "flex-start" : "flex-end",
            padding: "2px",
          }}
        >
          <div
            style={{
              width: "21px",
              height: "21px",
              backgroundColor: "white",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "12px",
              transition: "all 0.3s",
            }}
          >
            {isDarkTheme ? "☾" : "☀"}
          </div>
        </div>
      </div>

      <SearchField
        value={searchTerm}
        onChange={handleSearchTermChange}
        onSearch={handleSearch}
        isDarkTheme={isDarkTheme}
        loading={loading}
        includeClosedOrders={includeClosedOrders}
        onIncludeClosedOrdersChange={setIncludeClosedOrders}
      />

      {error && (
        <div style={{ padding: "20px", color: "red" }}>Error: {error}</div>
      )}
      {saveError && (
        <div style={{ padding: "20px", color: "red" }}>Save Error: {saveError}</div>
      )}
      {upsertError && (
        <div style={{ padding: "20px", color: "red" }}>Upsert Error: {upsertError}</div>
      )}
      {submitRequestError && (
        <div style={{ padding: "20px", color: "red" }}>Submit Request Error: {submitRequestError}</div>
      )}
      {stageError && (
        <div style={{ padding: "20px", color: "red" }}>Stage Error: {stageError}</div>
      )}
      <HeaderTable
        headers={updatedHeaders.length > 0 ? updatedHeaders : filteredHeaders}
        onHeaderClick={handleHeaderClick}
        selectedHeader={selectedHeader}
        isDarkTheme={isDarkTheme}
        onHeaderUpdate={(headerId, field, value) => {
          const headersToUpdate = updatedHeaders.length > 0 ? updatedHeaders : filteredHeaders;
          const updatedHeadersList = headersToUpdate.map(h => 
            h.requestNumber === headerId ? { ...h, toSite: value } : h
          );
          setUpdatedHeaders(updatedHeadersList);
        }}
        radSites={radSites}
        radSitesLoading={radSitesLoading}
        onLoadRadSites={fetchRadSites}
        onLoadMore={() => loadMoreHeaders(searchTerm, includeClosedOrders)}
        hasMore={hasMore}
        loading={loading}
      />

        <div style={{ marginTop: "10px", marginBottom: "10px" }}>
        <ScanFields
          scanFields={scanFields}
          onScanFieldsChange={setScanFields}
          onSerialNumberScan={serialScanner.scanSerialNumber}
          isDarkTheme={isDarkTheme}
          selectedHeaderToOrg={selectedHeader?.toOrg}
          lines={currentLines}
          clearSerialNotFoundRef={clearSerialNotFoundRef}
          serialScanLoading={serialScanLoading}
        />
      </div>

      {warningMessage && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: isDarkTheme ? '#8b4513' : '#fff3cd',
          color: '#856404',
          padding: '20px 30px',
          border: '2px solid #ffeaa7',
          borderRadius: '8px',
          fontSize: '18px',
          boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          gap: '15px',
          minWidth: '300px'
        }}>
          {warningMessage}
          <span
            onClick={() => setWarningMessage(null)}
            style={{
              cursor: 'pointer',
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#856404'
            }}
          >
            ×
          </span>
        </div>
      )}

      {linesError && (
        <div style={{ padding: "20px", color: "red" }}>
          Lines Error: {linesError}
        </div>
      )}

      <div style={{ marginBottom: "5px" }}>
        {linesLoading ? (
          <div style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            maxHeight: "375px",
            overflowY: "auto",
            border: `1px solid ${isDarkTheme ? '#555' : '#ccc'}`,
            backgroundColor: isDarkTheme ? '#2a2a2a' : '#ffffff'
          }}>
            <div
              style={{
                width: "40px",
                height: "40px",
                border: "4px solid #f3f3f3",
                borderTop: "4px solid #007bff",
                borderRadius: "50%",
                animation: "spin 1s linear infinite"
              }}
            />
          </div>
        ) : (
          <LinesTable
            lines={currentLines}
            isDarkTheme={isDarkTheme}
            onLineUpdate={lineManager.updateLine}
            selectedHeaderToOrg={selectedHeader?.toOrg}
            newRowIds={lineManager.newRowIds}
            scannedLineIds={scannedLineIds}
            clearErrorsRef={clearLinesErrorsRef}
          />
        )}
      </div>

      <ActionButtons
        isDarkTheme={isDarkTheme}
        onCancel={handleCancel}
        onSave={handleSave}
        onSubmit={handleSubmit}
        saveLoading={saveLoading}
        submitLoading={submitLoading}
      />

      {saveSuccessMessage && (
        <div style={{
          textAlign: 'center',
          backgroundColor: isDarkTheme ? '#2d5a2d' : '#d4edda',
          color: 'green',
          padding: '8px 16px',
          border: '1px solid green',
          borderRadius: '4px',
          fontSize: '14px',
          margin: '10px auto',
          width: 'fit-content',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          {saveSuccessMessage}
        </div>
      )}

      <SerialSelectionPopup
        isOpen={showSerialSelectionPopup}
        onClose={() => {
          setShowSerialSelectionPopup(false);
          setSerialValidationResults([]);
        }}
        onSelect={(selectedResult) => {
          serialScanner.processPopupSelection(selectedResult);
          
          // Stage all serial numbers except the selected one
          if (selectedHeader?.requestNumber && serialValidationResults.length > 0) {
            const selectedIndex = serialValidationResults.findIndex(r => r === selectedResult);
            const serialJson = constructStageSerialJson(
              selectedHeader.requestNumber,
              serialValidationResults,
              userId,
              selectedIndex
            );
            stageSerialNumbers(serialJson).catch(error => {
              console.error('Failed to stage serial numbers:', error);
            });
          }
          
          setShowSerialSelectionPopup(false);
          setSerialValidationResults([]);
        }}
        results={serialValidationResults}
        isDarkTheme={isDarkTheme}
      />

      <NewLineForm
        isOpen={showNewLineForm}
        onClose={() => setShowNewLineForm(false)}
        serialNumberFromLines={serialNumberForForm}
        onSave={async (newLine) => {
          // Helper function to match serial numbers with/without leading 'S'
          const matchesSerial = (scannedSerial: string, lineSerial: string): boolean => {
            const normalizeSerial = (serial: string) => serial.toLowerCase().replace(/^s/, '');
            return normalizeSerial(scannedSerial) === normalizeSerial(lineSerial);
          };
          
          // Create locator if all required fields are present
          let locator = newLine.toLocator || '';
          if (scanFields.pallet && scanFields.cage && scanFields.tote && scanFields.toSubinventory && selectedHeader?.toOrg && selectedHeader?.toSite && userId) {
            try {
              const createdLocator = await createLocator({
                p_user_id: userId,
                p_organization_name: selectedHeader.toSite,
                p_subinventory: scanFields.toSubinventory,
                p_pallet: scanFields.pallet,
                p_cage: scanFields.cage,
                p_tote: scanFields.tote
              });
              if (createdLocator) {
                locator = createdLocator;
                console.log('Created locator for new line form:', createdLocator);
              }
            } catch (error) {
              console.error('Failed to create locator for new line form:', error);
            }
          }
          
          const newLineWithLocator = { ...newLine, toLocator: locator };
          
          // Check if serial number already exists in lines table
          const existingLineIndex = currentLines.findIndex(line => 
            newLineWithLocator.serialNumber && line.serialNumber && matchesSerial(newLineWithLocator.serialNumber, line.serialNumber)
          );
          if (existingLineIndex !== -1) {
            // Update existing line
            setUpdatedLines(prev => {
              const baseLines = prev.length > 0 ? prev : headerLines;
              const updatedLines = [...baseLines];
              updatedLines[existingLineIndex] = {
                ...updatedLines[existingLineIndex],
                ...newLineWithLocator
              };
              return updatedLines;
            });
            setScannedLineIds(prev => new Set(prev).add(currentLines[existingLineIndex].id));
          } else {
            // Create new line
            const newLineWithId = {
              ...newLineWithLocator,
              id: `new-${Date.now()}`,
              validatedSerialItem: '',
              returnHeaderId: selectedHeader?.requestNumber || ''
            } as Line;
            setUpdatedLines(prev => {
              const baseLines = prev.length > 0 ? prev : headerLines;
              return [newLineWithId, ...baseLines];
            });
            setScannedLineIds(prev => new Set(prev).add(newLineWithId.id));
          }
          // Clear serial not found message
          if (clearSerialNotFoundRef.current) {
            clearSerialNotFoundRef.current();
          }
        }}
        scanFields={scanFields}
        isDarkTheme={isDarkTheme}
        selectedHeaderToOrg={selectedHeader?.toOrg}
        selectedHeaderFromSite={selectedHeader?.fromSite}
      />
    </div>
  );
};

export default Dashboard;
